import React, { memo, useMemo } from 'react';
import { PageContentProps } from '../../types/swipeable';
import { InfiniteHorizontalSwipe } from './InfiniteHorizontalSwipe';
import { PartialHorizontalSlide } from './PartialHorizontalSlide';

// カスタムHTML/CSSを別コンポーネント化（ちらつき防止）
const CustomContent = memo<{ html: string; embeddedCtaButtons: any[]; onEmbeddedCtaClick?: (button: any) => void }>(({ html, embeddedCtaButtons, onEmbeddedCtaClick }) => {
  const htmlContent = useMemo(() => {
    return html.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '').trim();
  }, [html]);

  const embeddedCtaHTML = useMemo(() => {
    return embeddedCtaButtons.map((btn: any) => {
      // 位置計算
      let leftStyle: string = '50%';
      let transformStyle: string = 'translateX(-50%)';
      
      switch (btn.leftPosition) {
        case 'center':
          leftStyle = '50%';
          transformStyle = 'translateX(-50%)';
          break;
        case 'left':
          leftStyle = '0';
          transformStyle = 'none';
          break;
        case 'right':
          leftStyle = '100%';
          transformStyle = 'translateX(-100%)';
          break;
        case 'custom':
          leftStyle = `${btn.leftPercentCustom ?? 0}%`;
          transformStyle = 'none';
          break;
      }
      
      const imgTag = btn.image 
        ? `<img src="${btn.image}" alt="${btn.name}" style="width: 100%; height: auto; display: block;" />` 
        : '';
      
      return `
        <div class="embedded-cta-button" data-cta-id="${btn.id}" style="
          position: absolute;
          bottom: ${btn.bottomPercent}%;
          left: ${leftStyle};
          transform: ${transformStyle};
          width: ${btn.widthPercent ?? 80}%;
          z-index: 500;
          cursor: pointer;
          pointer-events: auto;
        ">
          ${imgTag}
        </div>
      `;
    }).join('');
  }, [embeddedCtaButtons]);

  const handleClick = React.useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const ctaElement = target.closest('.embedded-cta-button') as HTMLElement | null;
    if (!ctaElement) return;

    const ctaId = ctaElement.getAttribute('data-cta-id');
    if (!ctaId) return;

    const clickedButton = embeddedCtaButtons.find((btn: any) => btn.id === ctaId);
    if (!clickedButton) return;

    event.preventDefault();
    event.stopPropagation();
    onEmbeddedCtaClick?.(clickedButton);
  }, [embeddedCtaButtons, onEmbeddedCtaClick]);

  return (
    <div 
      className="qwipe-custom-content"
      onClick={handleClick}
      dangerouslySetInnerHTML={{
        __html: `
          <style>
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              width: 100%;
              height: 100%;
              overflow: hidden;
            }
          </style>
          ${htmlContent}
          ${embeddedCtaHTML}
        `
      }}
    />
  );
});

CustomContent.displayName = 'CustomContent';

export const PageContent: React.FC<PageContentProps> = ({ 
  page, 
  horizontalPageIndex, 
  isCurrentPage, 
  site,
  isFirstPage = false,
  onEmbeddedCtaClick
}) => {
  const [isMuted, setIsMuted] = React.useState(true);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const customStyleRef = React.useRef<HTMLStyleElement | null>(null);

  // 埋め込み式CTAボタンの配列を取得
  const embeddedCtaButtons = React.useMemo(() => {
    if (!site?.settings?.ctaButtons) return [];
    
    let selectedButtons: any[] = [];
    
    // 選択されたボタンの配列を取得
    if (page?.ctaButtonIds && page.ctaButtonIds.length > 0) {
      selectedButtons = site.settings.ctaButtons.filter((btn: any) => 
        page.ctaButtonIds!.includes(btn.id)
      );
    } else if (page?.ctaButtonId) {
      const button = site.settings.ctaButtons.find((btn: any) => btn.id === page.ctaButtonId);
      if (button) {
        selectedButtons = [button];
      }
    }
    
    // 埋め込み式ボタンのみをフィルタリング
    return selectedButtons.filter((btn: any) => btn.displayMode === 'embedded');
  }, [site?.settings?.ctaButtons, page?.ctaButtonId, page?.ctaButtonIds]);

  // カスタムHTML/CSSをdocument.headに動的に挿入（CTAボタンと同様の実装）
  React.useEffect(() => {
    if (page.customHtml) {
      // 既存のスタイルを削除
      if (customStyleRef.current) {
        document.head.removeChild(customStyleRef.current);
        customStyleRef.current = null;
      }

      // 管理画面で登録されたHTMLから<style>タグの内容を抽出
      const styleMatch = page.customHtml.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
      
      if (styleMatch && styleMatch[1]) {
        // 新しいスタイル要素を作成
        const styleElement = document.createElement('style');
        styleElement.id = `qwipe-custom-styles-${page.id}`;
        
        // CSSを処理：@media内外でスコープを適切に適用
        let css = styleMatch[1];
        let processedCSS = '';
        
        // @mediaクエリを手動でパース（括弧のネストに対応）
        let i = 0;
        
        while (i < css.length) {
          const mediaMatch = css.substring(i).match(/@media\s*([^{]+)\{/);
          
          if (mediaMatch && mediaMatch.index !== undefined) {
            // @media外の通常のCSS
            const beforeMedia = css.substring(i, i + mediaMatch.index);
            if (beforeMedia.trim()) {
              processedCSS += beforeMedia.replace(/([^{}]+)\{([^}]*)\}/g, (m, selector, rules) => {
                if (selector.trim().startsWith('/*')) return m; // コメントはそのまま
                const scopedSelector = selector
                  .split(',')
                  .map((s: string) => `.qwipe-custom-content ${s.trim()}`)
                  .join(', ');
                return `${scopedSelector}{${rules}}`;
              });
            }
            
            // @media開始位置
            const mediaStart = i + mediaMatch.index + mediaMatch[0].length;
            let braceCount = 1;
            let mediaEnd = mediaStart;
            
            // 対応する閉じ括弧を探す
            while (mediaEnd < css.length && braceCount > 0) {
              if (css[mediaEnd] === '{') braceCount++;
              if (css[mediaEnd] === '}') braceCount--;
              mediaEnd++;
            }
            
            // @media内のCSS
            const mediaContent = css.substring(mediaStart, mediaEnd - 1);
            const scopedMediaContent = mediaContent.replace(/([^{}]+)\{([^}]*)\}/g, (m, selector, rules) => {
              if (selector.trim().startsWith('/*')) return m; // コメントはそのまま
              const scopedSelector = selector
                .split(',')
                .map((s: string) => `.qwipe-custom-content ${s.trim()}`)
                .join(', ');
              // 各CSSプロパティに!importantを追加
              const importantRules = rules
                .split(';')
                .map((rule: string) => {
                  const trimmed = rule.trim();
                  if (!trimmed) return '';
                  if (trimmed.includes('!important')) return trimmed;
                  return `${trimmed} !important`;
                })
                .filter((rule: string) => rule)
                .join('; ');
              return `${scopedSelector}{${importantRules}}`;
            });
            
            processedCSS += `@media ${mediaMatch[1]}{${scopedMediaContent}}`;
            i = mediaEnd;
          } else {
            // 残りの通常のCSS
            const remaining = css.substring(i);
            if (remaining.trim()) {
              processedCSS += remaining.replace(/([^{}]+)\{([^}]*)\}/g, (m, selector, rules) => {
                if (selector.trim().startsWith('/*')) return m; // コメントはそのまま
                const scopedSelector = selector
                  .split(',')
                  .map((s: string) => `.qwipe-custom-content ${s.trim()}`)
                  .join(', ');
                return `${scopedSelector}{${rules}}`;
              });
            }
            break;
          }
        }
        
        styleElement.textContent = processedCSS;
        document.head.appendChild(styleElement);
        customStyleRef.current = styleElement;
      }
    }

    // クリーンアップ
    return () => {
      if (customStyleRef.current) {
        document.head.removeChild(customStyleRef.current);
        customStyleRef.current = null;
      }
    };
  }, [page.customHtml, page.id]);

  // CSS変換関数（HTML部分のみ）
  const convertCSSWithScope = (htmlContent: string) => {
    if (!htmlContent) return '';
    
    return htmlContent.replace(/<style[^>]*>([\s\S]*?)<\/style>/gi, '');
  };

  // ページが変わった時の音声制御
  React.useEffect(() => {
    if (videoRef.current && page.hasVideo) {
      if (isCurrentPage && isFirstPage) {
        // 1ページ目で現在のページの場合、ユーザーが設定した音声状態を維持
        videoRef.current.muted = isMuted;
      } else {
        // 1ページ目以外、または現在のページでない場合は必ずミュート
        videoRef.current.muted = true;
      }
    }
  }, [isCurrentPage, page.pageId, page.hasVideo, isMuted]);

  const toggleAudio = () => {
    if (videoRef.current && isFirstPage) {
      const newMutedState = !isMuted;
      setIsMuted(newMutedState);
      videoRef.current.muted = newMutedState;
      
      // 音声を有効にした場合、再生が止まっていたら再開
      if (!newMutedState && videoRef.current.paused) {
        videoRef.current.play();
      }
    }
  };

  return (
    <div className="qwipe-page-content">
      {/* カスタムHTML/CSS表示 */}
      {page.customHtml && (
        <CustomContent
          html={page.customHtml}
          embeddedCtaButtons={embeddedCtaButtons}
          onEmbeddedCtaClick={onEmbeddedCtaClick}
        />
      )}
      
      {/* customHtmlがない場合でも埋め込み式CTAボタンを表示 */}
      {!page.customHtml && embeddedCtaButtons.length > 0 && embeddedCtaButtons.map((btn: any) => {
        // 位置計算
        let leftStyle: string = '50%';
        let transformStyle: string = 'translateX(-50%)';
        
        switch (btn.leftPosition) {
          case 'center':
            leftStyle = '50%';
            transformStyle = 'translateX(-50%)';
            break;
          case 'left':
            leftStyle = '0';
            transformStyle = 'none';
            break;
          case 'right':
            leftStyle = '100%';
            transformStyle = 'translateX(-100%)';
            break;
          case 'custom':
            leftStyle = `${btn.leftPercentCustom ?? 0}%`;
            transformStyle = 'none';
            break;
        }
        
        return (
          <div 
            key={btn.id}
            className="embedded-cta-button" 
            data-cta-id={btn.id}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onEmbeddedCtaClick?.(btn);
            }}
            style={{
              position: 'absolute',
              bottom: `${btn.bottomPercent}%`,
              left: leftStyle,
              transform: transformStyle,
              width: `${btn.widthPercent ?? 80}%`,
              zIndex: 500,
              cursor: 'pointer'
            }}
          >
            {btn.image && (
              <img 
                src={btn.image} 
                alt={btn.name}
                loading="lazy"
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  display: 'block' 
                }} 
              />
            )}
          </div>
        );
      })}
      
      {/* Page01の動画表示 */}
      {page.hasVideo && page.videoUrl && (
        <div className="qwipe-video-container">
          <video 
            ref={videoRef}
            src={page.videoUrl}
            autoPlay
            loop
            muted
            playsInline
            className="qwipe-page-video"
          />
          {isFirstPage && (
            <div className="qwipe-video-controls">
              <button 
                className="volume-toggle"
                onClick={toggleAudio}
                title={isMuted ? '音声をオンにする' : '音声をオフにする'}
              >
                {isMuted ? '🔇' : '🔊'}
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* 横スワイプページの詳細部分表示 */}
      {page.isHorizontal && page.subPageIds && (
        <div className="qwipe-horizontal-detail">
          <InfiniteHorizontalSwipe 
            subPageIds={page.subPageIds}
            currentIndex={horizontalPageIndex}
          />
          
          {/* 横スワイプインジケーター */}
          <div className="qwipe-horizontal-indicator">
            {page.subPageIds.map((_, index) => {
              const displayIndex = ((horizontalPageIndex % page.subPageIds!.length) + page.subPageIds!.length) % page.subPageIds!.length;
              return (
                <div
                  key={index}
                  className={`qwipe-indicator-dot ${index === displayIndex ? 'active' : ''}`}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* 横部分スライド（通常の横スワイプとは完全に別機能） */}
      {page.horizontalSlides && page.horizontalSlides.length > 0 && (
        <div className="partial-horizontal-slide-section">
          <PartialHorizontalSlide 
            slides={page.horizontalSlides}
            className="partial-horizontal-slide-main"
            slideWidth={page.horizontalSlideWidth ?? 100}
            viewportWidth={page.horizontalSlideViewportWidth}
            indicatorPosition={page.horizontalSlideIndicatorPosition || 'bottom'}
            infiniteLoop={page.horizontalSlideInfiniteLoop !== false}
            initialIndex={page.horizontalSlideInitialIndex ?? 1}
            positionAdjustment={page.horizontalSlidePositionAdjustment ?? 0}
            indicatorOffsetX={page.horizontalSlideIndicatorOffsetX ?? 0}
            indicatorOffsetY={page.horizontalSlideIndicatorOffsetY ?? 0}
            pageId={page.id}
          />
        </div>
      )}
    </div>
  );
};

